import { Box, Collapse, Grid, TableCell, TableRow, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import BatchShippingIcon, { batchShippingTooltip } from 'common/components/BatchShippingIcon';
import BusinessAddressIndicator from 'common/components/BusinessAddressIndicator';
import ReshippingIcon from 'common/components/ReshippingIcon';
import ShipByDateCell from 'common/components/ShipByDateCell';
import orderStatus from 'common/constants/orderStatus';
import perfectRxTheme from 'common/theme/perfectRxTheme';
import { casingFormat, sortTags } from 'common/utils';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { orderSource } from 'enums/order.enum';
import { capitalize, isBoolean, upperCase } from 'lodash';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { updateBatchShipmentFilter } from 'store/thunks/batchShipmentThunk';
import OrderVerificationLineItemsRow from './OrderVerificationLineItemsRow';
import OrderVerificationOptions from './OrderVerificationOptions';
import { TagsTableCell } from './WorkQueueTagsComponents';

const OrderVerificationRow = ({
  order,
  partnerAccountHandler,
  currentPage,
  loadingLineItems,
  formatTagText,
  handleTagsClick,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const getPartnerAccountName = (partnerAccountId) => {
    return partnerAccountId && partnerAccountHandler[partnerAccountId]
      ? capitalize(partnerAccountHandler[partnerAccountId].partnerAccountName)
      : 'N/A';
  };

  const navigateToBatchShipmentQueue = (orderId) => {
    dispatch(updateBatchShipmentFilter({ filter: { orderId } }));
    navigate('/batchShipments');
  };

  const { mpi, patientName, orderId, shipByDate, requestNeedsByDate, createdAt, state } = order;
  const { lastUpdated, orderStatus: status, waitingOnFill, totalInOrder, isReshipment } = order;
  const { partnerName, partnerAccountId, inBatchShipmentQueue } = order;
  const { isBusinessAddress, isOtc, quantity } = order;

  return (
    <>
      <TableRow hover>
        <TableCell align='center'>
          <Link to={`/orders/${orderId}`} style={{ color: theme.palette.text.primary }}>
            {orderId}
          </Link>
        </TableCell>
        <TableCell component='th' scope='row' align='center'>
          <Link to={`/patients/${mpi}`} style={{ color: theme.palette.text.primary }}>
            {patientName}
          </Link>
        </TableCell>
        <TableCell align='center'>{order.drugName}</TableCell>
        <TableCell align='center'>{isBoolean(isOtc) && isOtc ? 'OTC' : 'RX'}</TableCell>
        <TableCell align='center'>{quantity}</TableCell>
        <TableCell align='center'>{casingFormat(partnerName)}</TableCell>
        <TableCell align='center'>{getPartnerAccountName(partnerAccountId)}</TableCell>
        <TableCell align='center'>
          <ShipByDateCell shipByDate={shipByDate} tags={order.tags} />
        </TableCell>
        <TableCell align='center' sx={{ color: 'text.disabled' }}>
          <Tooltip title='Legacy field — see Ship By Date' arrow>
            <span>
              {requestNeedsByDate
                ? format(utcToZonedTime(new Date(requestNeedsByDate), 'utc'), 'MM/dd/yyyy')
                : '—'}
            </span>
          </Tooltip>
        </TableCell>
        <TableCell align='center'>{upperCase(state)}</TableCell>
        <TableCell align='center'>{orderStatus[status]}</TableCell>
        <TableCell align='center'>
          <div>{format(new Date(createdAt), 'MM/dd/yyyy')}</div>
          <div>{format(new Date(createdAt), 'hh:mm a')}</div>
        </TableCell>
        <TableCell align='center'>
          <div>{format(new Date(lastUpdated), 'MM/dd/yyyy')}</div>
          <div>{format(new Date(lastUpdated), 'hh:mm a')}</div>
        </TableCell>
        <TableCell align='center'>{waitingOnFill}</TableCell>
        <TableCell align='center'>{totalInOrder}</TableCell>
        <TableCell>
          <Tooltip title={orderSource[order.source].sourceValue} arrow placement='top'>
            {orderSource[order.source].sourceKey}
          </Tooltip>
        </TableCell>
        <TableCell>
          <TagsTableCell
            lineItem={{
              lineItemId: order.lineItemId,
              tags: order.tags ? sortTags(order.tags) : [],
            }}
            isLoading={loadingLineItems?.has(order.lineItemId)}
            formatTagText={formatTagText}
            handleTagsClick={handleTagsClick}
          />
        </TableCell>
        <TableCell>
          <Tooltip
            title={
              inBatchShipmentQueue
                ? batchShippingTooltip({ isBusinessAddress, isClickable: true })
                : ''
            }
            arrow
            placement='top'
          >
            {inBatchShipmentQueue ? (
              <BatchShippingIcon
                onClick={() => navigateToBatchShipmentQueue(orderId)}
                color={perfectRxTheme.palette.warning.warningColour}
                showBadge={isBusinessAddress}
              />
            ) : (
              <Grid>{isBusinessAddress && <BusinessAddressIndicator />}</Grid>
            )}
          </Tooltip>
        </TableCell>
        <TableCell>
          {isReshipment ? (
            <Tooltip title='Item being processed as a reshipment' arrow placement='top'>
              <span>
                <ReshippingIcon />
              </span>
            </Tooltip>
          ) : (
            <Grid />
          )}
        </TableCell>
        <TableCell align='center'>
          <OrderVerificationOptions order={order} currentPage={currentPage} />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={8} sx={{ p: 0, border: 'none' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Collapse timeout='auto' sx={{ width: '100%' }}>
              {order?.lineItems && (
                <OrderVerificationLineItemsRow
                  lineItemRow={order.orderId}
                  lineItems={order.lineItems}
                  fulfillingPharmacyId={order.fulfillingPharmacyId}
                />
              )}
            </Collapse>
          </Box>
        </TableCell>
      </TableRow>
    </>
  );
};

OrderVerificationRow.propTypes = {
  order: PropTypes.shape({
    mpi: PropTypes.string.isRequired,
    patientName: PropTypes.string.isRequired,
    orderId: PropTypes.string.isRequired,
    lineItemId: PropTypes.string.isRequired,
    drugName: PropTypes.string,
    requestNeedsByDate: PropTypes.string.isRequired,
    shipByDate: PropTypes.string,
    createdAt: PropTypes.string.isRequired,
    lastUpdated: PropTypes.string.isRequired,
    orderStatus: PropTypes.string.isRequired,
    waitingOnFill: PropTypes.number.isRequired,
    totalInOrder: PropTypes.number.isRequired,
    state: PropTypes.string,
    fulfillingPharmacyId: PropTypes.string.isRequired,
    source: PropTypes.number,
    isReshipment: PropTypes.bool,
    inBatchShipmentQueue: PropTypes.bool,
    isBusinessAddress: PropTypes.bool,
    isOtc: PropTypes.bool,
    quantity: PropTypes.string,
    lineItems: PropTypes.arrayOf(
      PropTypes.shape({
        lineItemId: PropTypes.string,
        itemName: PropTypes.string,
        rxNumber: PropTypes.string,
        strength: PropTypes.string,
        form: PropTypes.string,
        quantity: PropTypes.string,
        fillType: PropTypes.string,
        needsByDate: PropTypes.string,
      })
    ),
    partnerName: PropTypes.string.isRequired,
    partnerAccountId: PropTypes.string,
    tags: PropTypes.arrayOf(
      PropTypes.shape({
        tagName: PropTypes.string,
        isSystemTag: PropTypes.bool,
      })
    ),
  }).isRequired,
  partnerAccountHandler: PropTypes.objectOf(
    PropTypes.shape({
      partnerAccountName: PropTypes.string.isRequired,
      partnerAccountId: PropTypes.string,
    })
  ).isRequired,
  currentPage: PropTypes.number.isRequired,
  loadingLineItems: PropTypes.instanceOf(Set),
  formatTagText: PropTypes.func,
  handleTagsClick: PropTypes.func,
};

OrderVerificationRow.defaultProps = {
  loadingLineItems: new Set(),
  formatTagText: undefined,
  handleTagsClick: undefined,
};

export default OrderVerificationRow;
